import { createSelector, current } from '@reduxjs/toolkit';
import {
  applicant_required_fields,
  applicant_qi_required_fields,
  app_section_ids,
  APPLICATION_STATUSES,
  application_cover_products,
} from '../constants/application';
import { isApplicantsCoversBenefitAllocationsValid } from '../utilities/application/beneficiaryHelper';
import { formatDate, getAge } from '../utilities/date/dateHelper';
import { contact_postcode_validation } from '../constants/contact';
import { examinerValid } from '../utilities/application/amraHelper';

export const getApplication = (state) => state.application;
export const getApplicationId = (state) => state.application.id;
export const getApplicationLoading = (state) => state.application.loading;
export const getApplicationApplicantStatusApplicants = (state) =>
  state.application.applicantStatus.applicants;
export const getApplicantsCoverLoading = (state) =>
  state.application.applicantStatus.cover.loading;
export const getApplicants = (state) => state.application.applicants;
export const getEnquiries = (state) => state.application.applicantsEnquiries;
export const getMortgageRelated = (state) =>
  state.application.isMortgageRelated;
export const getThirdPartyRef = (state) => state.application.thirdPartyRef;
export const getInitialCommissionSacrificePercentage = (state) =>
  state.application.initialCommissionSacrificePercentage;
export const getInitialIndemnityPercentage = (state) =>
  state.application.initialIndemnityPercentage;
export const getApplicationStatus = (state) =>
  state.application.applicationStatus;
export const getApplicantsStatus = (state) =>
  state.application.applicantStatus.applicants;
export const getApplicationStatusLoading = (state) =>
  state.application.applicantStatus.status.loading;
export const getActivateLoading = (state) =>
  state.application.applicantStatus.activate.loading;
export const getActivateErrors = (state) =>
  state.application.applicantStatus.activate.error;
export const getApplicationActive = (state) => state.application.active;
export const getApplicationChildren = (state) => state.application.children;
export const getApplicationChildrenLoading = (state) =>
  state.application.applicantStatus.children.loading;
export const getApplicationBankDetails = (state) =>
  state.application.bankDetails;
export const getBankValidating = (state) =>
  state.application.applicantStatus.bankDetails.validating;
export const getCandidateBeneficiaries = (state) =>
  state.application.candidateBeneficiaries;
export const getApplicationStatusBeneficiariesLoading = (state) =>
  state.application.applicantStatus.beneficiaries.loading;
export const getUnderwritingUpdates = (state) =>
  state.application.applicantsUnderwritingUpdates;
export const getRestrictedCover = (state) =>
  state.application.isRestrictedCover;
export const getHasUnbornChildren = (state) =>
  state.application.hasUnbornChildren;
export const getHasUnbornChildrenLoading = (state) =>
  state.application.applicantStatus.hasUnbornChildren.loading;
export const getPortalLoading = (state) =>
  state.application.applicantStatus.portal.loading;
export const getPortalApplicationId = (state) =>
  state.application.applicantStatus.portal.applicationId;
export const getPortalError = (state) =>
  state.application.applicantStatus.portal.error;
export const getRequoteLoading = (state) =>
  state.application.applicantStatus.requote.loading;
export const getApplicationUenrichedLoading = (state) =>
  state.application.applicantStatus.unenriched.loading;
export const getApplicationQuoteAgeDate = (state) =>
  state.application.quoteAgeDate;
export const getExtendedOnDate = (state) => state.application.extendedOnDate;
export const isTermExtensible = (state) =>
  state.application.isTermExtensible === true &&
  state.application.extendedOnDate === null;
export const getTermExtensibleApplicantEnquiries = (state) =>
  state.application.termExtensible?.applicantEnquiries;
export const getIsPortalQuote = (state) => state.application.isPortalQuote;

export const getApplicationUnenrichedApplicants = (state) => {
  try {
    return state.application.applicationUnenriched.applicants;
  } catch {
    return null;
  }
};

export const getApplicant1Name = createSelector(getApplicants, (applicants) => {
  if (applicants && applicants[0] && applicants[0].firstName) {
    return `${applicants[0].firstName} ${applicants[0].lastName}`;
  }
  return '';
});

export const getApplicant2Name = createSelector(getApplicants, (applicants) => {
  if (applicants && applicants[1] && applicants[1].firstName) {
    return `${applicants[1].firstName} ${applicants[1].lastName}`;
  }
  return '';
});

export const getApplicant1FirstName = createSelector(
  getApplicants,
  (applicants) => {
    if (applicants && applicants[0] && applicants[0].firstName) {
      return applicants[0].firstName;
    }
    return '';
  }
);

export const getApplicant2FirstName = createSelector(
  getApplicants,
  (applicants) => {
    if (applicants && applicants[1] && applicants[1].firstName) {
      return applicants[1].firstName;
    }
    return '';
  }
);

const getApplicationIdSelector = createSelector(
  [getApplicationId],
  (getApplicationId) => {
    return getApplicationId;
  }
);

const getApplicationLoadingSelector = createSelector(
  [getApplicationLoading],
  (getApplicationLoading) => {
    return getApplicationLoading;
  }
);

export const ridersValid = (cover) => {
  if (!cover.riders || cover.riders.length === 0) {
    return true;
  }
  if (
    cover.riders &&
    cover.riders.filter(
      (rider) => !rider.coverAmount || rider.coverAmount.length <= 0
    ).length <= 0
  ) {
    return true;
  }
  return false;
};

const coverValid = (cover) => {
  if (cover.productType === application_cover_products.CCIC) {
    return !!cover.coverType && !!cover.coverAmount;
  }
  return (
    !!cover.coverType &&
    (!!cover.coverAmount || !!cover.monthlyBenefit) &&
    (!!cover.term || !!cover.toAge)
  );
};

export const canGetQuotePrice = createSelector(getApplicants, (applicants) => {
  if (
    applicants.filter((applicant) => applicant.covers.length > 0).length === 0
  ) {
    return false;
  }
  return (
    applicants.filter(
      (applicant) =>
        applicant.covers.length === 0 ||
        applicant.covers.filter(
          (cover) => cover.id && !cover.errors && coverValid(cover)
        ).length === applicant.covers.length
    ).length === applicants.length
  );
});

/**
 * Get the applicant by the applicantId from the application applicants array
 * called like this useSelector((state) => getApplicantByApplicantId(state, applicantId));
 */
export const getApplicantByApplicantId = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.find((applicant) => applicant.id === applicantId)
);

export const getApplicantNameByApplicantId = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => {
    if (applicant) {
      return `${applicant.firstName} ${applicant.lastName}`;
    }
    return null;
  }
);

/**
 * Get the applicant by the index from the application applicants array
 */
export const getApplicantByIndex = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants, index) => (applicants[index] ? applicants[index] : null)
);
/**
 * Get the applicant index from the application applicants array
 */
export const getApplicantIndex = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.findIndex((applicant) => applicant.id === applicantId)
);

export const getTermExtentionApplicantIndex = createSelector(
  [getTermExtensibleApplicantEnquiries, (state, applicantId) => applicantId],
  (applicantEnquiries, applicantId) =>
    applicantEnquiries.findIndex(
      (applicant) => applicant.applicantId === applicantId
    )
);

export const getTermExtentionEnquiryByApplicantId = createSelector(
  [getTermExtensibleApplicantEnquiries, (state, applicantId) => applicantId],
  (applicantEnquiries, applicantId) =>
    applicantEnquiries.find(
      (applicant) => applicant.applicantId === applicantId
    )
);

export const getTermExtentionExclusivesByApplicantId = createSelector(
  getTermExtentionEnquiryByApplicantId,
  (applicantEnquiry) =>
    applicantEnquiry.enquiry && applicantEnquiry.enquiry.exclusives
      ? applicantEnquiry.enquiry.exclusives
      : []
);

/**
 * Get the enquiry index from the application applicants array
 */
export const getEnquiryIndex = createSelector(
  [getEnquiries, (state, applicantId) => applicantId],
  (enquiries, applicantId) =>
    enquiries.findIndex((enquiry) => enquiry.id === applicantId)
);

/**
 * Get the applicantId for the first applicant
 */
export const getApplicant1Id = createSelector(getApplicants, (applicants) =>
  applicants[0] ? applicants[0].id : null
);

/**
 * Get the applicantId for the second applicant
 */
export const getApplicant2Id = createSelector(getApplicants, (applicants) =>
  applicants[1] ? applicants[1].id : null
);

export const getApplicantAge = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? getAge(applicant.dateOfBirth) : null)
);

export const getApplicantDateOfBirth = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.dateOfBirth : null)
);

export const getApplicantIsAmraConsentGranted = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.isAmraConsentGranted : null)
);

export const getApplicantIsViewMedicalReportsRequested = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.isViewMedicalReportsRequested : null)
);

export const getApplicantAmraConsentComplete = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) =>
    applicant &&
    applicant.isViewMedicalReportsRequested !== null &&
    applicant.isAmraConsentGranted === true
);

export const getApplicantAmraComplete = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) =>
    applicant &&
    applicant.isViewMedicalReportsRequested !== null &&
    applicant.isAmraConsentGranted === true &&
    applicant.examiner !== null &&
    examinerValid(applicant.examiner)
);

export const getApplicantAmraDoctorComplete = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) =>
    applicant &&
    applicant.examiner !== null &&
    examinerValid(applicant.examiner)
);

export const getApplicantExaminer = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => {
    if (applicant && applicant.examiner) {
      const { contactDetails, ...props } = applicant.examiner;
      return {
        ...props,
        ...contactDetails,
      };
    }
    return null;
  }
);

export const getApplicantCoversByApplicantId = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.covers : null)
);

export const doApplicantHaveCovers = createSelector(
  [getApplicantCoversByApplicantId, (state, applicantId) => applicantId],
  (covers) => covers && covers.length > 0
);

export const doApplicantsHaveCovers = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter((applicant) => applicant.covers.length > 0).length > 0
);

export const doApplicantsHaveValidCovers = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) =>
        applicant.covers.length > 0 &&
        applicant.covers.findIndex((cover) => cover.id) !== -1
    ).length > 0
);

export const getApplicantTabs = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants) =>
    applicants.map((applicant) => ({
      applicantId: applicant.id,
      name: `${applicant.firstName} ${applicant.lastName}`,
    }))
);

export const getApplicantIndexFromMeta = (state, action) => {
  let applicantId = action.meta.arg.applicantId
    ? action.meta.arg.applicantId
    : action.meta.arg;

  let index = getApplicantIndex({ application: current(state) }, applicantId);
  return index;
};

export const getApplicantIndexFromPayloadData = (state, action) => {
  const { id } = action.payload.data;
  let index = getApplicantIndex({ application: current(state) }, id);
  return index;
};

export const getApplicantIndexFromPayload = (state, action) => {
  const { applicantId } = action.payload;
  let index = getApplicantIndex({ application: current(state) }, applicantId);
  return index;
};

export const getEnquiryIndexFromPayload = (state, action) => {
  const { applicantId } = action.payload;
  let index = getEnquiryIndex({ application: current(state) }, applicantId);
  return index;
};

export const getApplicantsIsSatisfied = createSelector(
  getApplicationApplicantStatusApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) => applicant.isSatisfied === false && applicant.applicantId
    ).length <= 0
);

export const getApplicantsLoading = createSelector(
  getApplicationApplicantStatusApplicants,
  (applicants) =>
    applicants.filter((applicant) => applicant.loading === true).length > 0
);

export const getApplicantsContactDetails = createSelector(
  getApplicants,
  (applicants) =>
    applicants
      .filter((applicant) => applicant.id !== null)
      .map((applicant) => ({
        id: applicant.id,
        title: applicant.title,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        ...applicant.contactDetails,
      }))
);

export const doApplicantsHaveCCICCover = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) =>
        applicant.covers.findIndex(
          (cover) =>
            (cover.riders && cover.riders.length > 0) ||
            cover.productType === application_cover_products.CCIC
        ) !== -1
    ).length > 0
);

export const getAcceptedPreUnderwritingDeclarations = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) => applicant.isAcceptedPreUnderwritingDeclaration === true
    ).length === 0
);

export const getAcceptedPreUnderwritingDeclarationByApplicantId =
  createSelector(
    [getApplicantByApplicantId, (state, applicantId) => applicantId],
    (applicant) =>
      applicant ? applicant.isAcceptedPreUnderwritingDeclaration : false
  );

export const getPolicyStartDate = createSelector(
  getApplication,
  (application) => {
    if (application.policyStartDate) {
      return formatDate(application.policyStartDate);
    }
    if (
      application.policyStartDateOption &&
      application.policyStartDateOption === 'now'
    ) {
      return formatDate(Date.now());
    }
    return null;
  }
);

export const getIsChildrenComplete = createSelector(
  getApplication,
  (application) => {
    if (application.hasUnbornChildren === true) return true;
    if (application.children && application.children.length > 0) return true;
    return false;
  }
);

export const isClientDetailsComplete = createSelector(
  getApplication,
  (application) => {
    try {
      return application.sections.find(
        (section) => section.id === app_section_ids.CLIENT_DETAILS
      ).isComplete;
    } catch {
      return false;
    }
  }
);

export const isUnderwritingComplete = createSelector(
  getApplication,
  (application) => {
    try {
      return application.sections.find(
        (section) => section.id === app_section_ids.UNDERWRITING
      ).isComplete;
    } catch {
      return false;
    }
  }
);

export const isUnderwritingStatus = createSelector(
  getApplicationStatus,
  (applicationStatus) => applicationStatus === APPLICATION_STATUSES.UNDERWRITING
);

export const getApplicantErrorsByApplicantId = createSelector(
  [getApplicantsStatus, (state, applicantId) => applicantId],
  (applicants, applicantId) => {
    const applicant = applicants.find(
      (applicant) => applicant.applicantId === applicantId
    );
    if (applicant) {
      return applicant.error;
    }
    return [];
  }
);

export const getSameAddressApplicantId = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.sameAddressApplicantId : null)
);

export const getIsValidContactDetails = createSelector(
  getApplicants,
  (applicants) =>
    applicants
      .filter((applicant) => applicant.id !== null)
      .filter(
        (applicant) =>
          (applicant.contactDetails &&
            applicant.contactDetails.address1 &&
            applicant.contactDetails.address1 !== '' &&
            applicant.contactDetails.address1 &&
            (!applicant.contactDetails.address1 ||
              applicant.contactDetails.address1.length <= 40) &&
            (!applicant.contactDetails.address2 ||
              applicant.contactDetails.address2.length <= 40) &&
            (!applicant.contactDetails.address3 ||
              applicant.contactDetails.address3.length <= 40) &&
            applicant.contactDetails.postcode &&
            applicant.contactDetails.postcode !== '' &&
            contact_postcode_validation.test(
              applicant.contactDetails.postcode
            )) ||
          applicant.sameAddressApplicantId
      ).length === applicants.length
);

export const getIsSameApplicantEmailAddress = createSelector(
  getApplicants,
  (applicants) => {
    try {
      if (applicants.length === 1) return false;
      if (
        applicants.length === 2 &&
        applicants[0].contactDetails.emailAddress.length > 0 &&
        applicants[0].contactDetails.emailAddress ===
          applicants[1].contactDetails.emailAddress
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
);

export const getApplicantRiskDecisions = createSelector(
  [getApplicantByApplicantId, (state, applicantId) => applicantId],
  (applicant) => {
    try {
      return applicant.riskDecisions;
    } catch {
      return [];
    }
  }
);

export const getApplicantUnderwritingUpdatesByApplicantId = createSelector(
  [getUnderwritingUpdates, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.find((applicant) => applicant.id === applicantId)
);

export const getApplicantsBeneficiariesValid = createSelector(
  [getApplicants, (state, quoteApplicants) => quoteApplicants],
  (applicants, quoteApplicants) =>
    isApplicantsCoversBenefitAllocationsValid(applicants, quoteApplicants)
);

export const isCoverErrorRequote = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) =>
        applicant.covers.findIndex(
          (cover) => cover.errors && cover.errors.status === 412
        ) !== -1
    ).length > 0
);

export const isCoverErrors = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter(
      (applicant) =>
        applicant.covers.findIndex(
          (cover) => cover.errors && cover.errors.length > 0
        ) !== -1
    ).length > 0
);

export const isApplicantsPolicyReferenceValid = createSelector(
  getApplicationUnenrichedApplicants,
  (applicants) => {
    try {
      if (!applicants) throw new Error('no applicants');
      return (
        applicants.filter((applicant) => applicant.policyReference !== null)
          .length === applicants.length
      );
    } catch {
      return false;
    }
  }
);

export const isApplicantStatisfied = (applicant) =>
  applicant_required_fields.filter(
    (field) => applicant[field] === null || applicant[field] === ''
  ).length === 0;

export const isApplicantsStatisfied = (applicants) =>
  applicants.filter((applicant) => !isApplicantStatisfied(applicant)).length ===
  0;

export const isQIApplicantStatisfied = (applicant) =>
  applicant_qi_required_fields.filter(
    (field) => applicant[field] === null || applicant[field] === ''
  ).length === 0;

export const isQIApplicantsStatisfied = (applicants) =>
  applicants.filter((applicant) => !isQIApplicantStatisfied(applicant))
    .length === 0;

/**
 * Get a cover from an applicant by id
 *
 * @param {Array} applicants
 * @param {String} applicantId
 * @param {String} coverId
 */
export const getApplicantCover = (applicants, applicantId, coverId) => {
  try {
    const covers = applicants
      .filter((applicant) => applicant.id === applicantId)
      .map((applicant) =>
        applicant.covers.find((cover) => cover.id === coverId)
      );
    if (covers && covers.length > 0) {
      return covers[0];
    }
    throw new Error('Cover not found');
  } catch (error) {
    throw new Error(error);
  }
};

export default {
  getApplicationIdSelector,
  getApplicationLoadingSelector,
  canGetQuotePrice,
};
